import React, { useState, useMemo, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Stack, Chip, TextField,
  InputAdornment, Button, Tooltip, IconButton, Paper, Dialog,
  DialogTitle, DialogContent, DialogActions, Avatar
} from '@mui/material';
import SearchIcon        from '@mui/icons-material/Search';
import DownloadIcon      from '@mui/icons-material/Download';
import ContentCopyIcon   from '@mui/icons-material/ContentCopy';
import AccessTimeIcon    from '@mui/icons-material/AccessTime';
import EditIcon          from '@mui/icons-material/Edit';
import CloseIcon         from '@mui/icons-material/Close';
import PersonIcon        from '@mui/icons-material/Person';
import CheckIcon         from '@mui/icons-material/Check';
import { saveTranscript } from '../api';

/* ── Speaker colour palette ── */
const SPEAKER_COLORS = [
  { bg: 'rgba(139,92,246,0.08)',  border: '#8B5CF6', dot: '#8B5CF6', text: '#A78BFA', bar: '#7C3AED'  },
  { bg: 'rgba(6,182,212,0.08)',   border: '#06B6D4', dot: '#06B6D4', text: '#22D3EE', bar: '#0891B2'  },
  { bg: 'rgba(16,185,129,0.08)',  border: '#10B981', dot: '#10B981', text: '#34D399', bar: '#059669'  },
  { bg: 'rgba(245,158,11,0.08)',  border: '#F59E0B', dot: '#F59E0B', text: '#FCD34D', bar: '#D97706'  },
  { bg: 'rgba(236,72,153,0.08)',  border: '#EC4899', dot: '#EC4899', text: '#F472B6', bar: '#BE185D'  },
  { bg: 'rgba(59,130,246,0.08)',  border: '#3B82F6', dot: '#3B82F6', text: '#60A5FA', bar: '#1D4ED8'  },
];

function fmtTime(sec) {
  return `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(Math.floor(sec % 60)).padStart(2, '0')}`;
}

export default function TranscriptViewer({ meeting }) {
  const [search, setSearch]               = useState('');
  const [copied, setCopied]               = useState(false);
  const [speakerMap, setSpeakerMap]       = useState({});
  const [editingSpeaker, setEditingSpeaker] = useState(null);
  const [newSpeakerName, setNewSpeakerName] = useState('');
  const [isSaving, setIsSaving]           = useState(false);
  const [activeSpeaker, setActiveSpeaker] = useState(null);

  /* ── Initialise speaker display names ── */
  useEffect(() => {
    if (meeting?.transcript) {
      const names = [...new Set(meeting.transcript.map(s => s.speaker))];
      const map = {};
      names.forEach(n => { map[n] = n; });
      setSpeakerMap(map);
    }
  }, [meeting]);

  const transcript = useMemo(() => (meeting?.transcript || []).map(s => ({
    ...s,
    displayName: speakerMap[s.speaker] || s.speaker,
  })), [meeting, speakerMap]);

  const colorMap = useMemo(() => {
    const map = {};
    [...new Set(transcript.map(s => s.speaker))].forEach((n, i) => {
      map[n] = SPEAKER_COLORS[i % SPEAKER_COLORS.length];
    });
    return map;
  }, [transcript]);

  const speakerNames = Object.keys(colorMap);

  /* ── Filtered segments ── */
  const displaySegments = useMemo(() => {
    const q = search.toLowerCase().trim();
    return transcript.filter(s => {
      const matchSpeaker = !activeSpeaker || s.speaker === activeSpeaker;
      const matchSearch  = !q || s.text.toLowerCase().includes(q) || s.displayName.toLowerCase().includes(q);
      return matchSpeaker && matchSearch;
    });
  }, [transcript, search, activeSpeaker]);

  /* ── Actions ── */
  const handleCopy = () => {
    const text = transcript.map(s => `[${fmtTime(s.start)}] ${s.displayName}: ${s.text}`).join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDownload = () => {
    const lines = [
      `Meeting: ${meeting.title || meeting.meeting_id}`,
      `Date: ${meeting.created_at ? new Date(meeting.created_at).toLocaleString() : 'N/A'}`,
      '', '--- TRANSCRIPT ---', '',
      ...transcript.map(s => `[${fmtTime(s.start)}] ${s.displayName}: ${s.text}`),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript_${(meeting.meeting_id || 'meeting').substring(0, 8)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openEditSpeaker = (original) => {
    setEditingSpeaker(original);
    setNewSpeakerName(speakerMap[original] || original);
  };

  const handleSaveSpeaker = async () => {
    if (!editingSpeaker || !newSpeakerName.trim()) return;
    setSpeakerMap(prev => ({ ...prev, [editingSpeaker]: newSpeakerName.trim() }));
    setIsSaving(true);
    try { await saveTranscript(meeting.meeting_id, meeting.full_text, meeting.language, transcript); } catch {}
    finally { setIsSaving(false); }
    setEditingSpeaker(null);
  };

  /* ── Search highlight ── */
  const highlight = (text, q) => {
    if (!q.trim()) return text;
    const parts = text.split(new RegExp(`(${q})`, 'gi'));
    return parts.map((p, i) =>
      p.toLowerCase() === q.toLowerCase()
        ? <mark key={i} style={{ background: 'rgba(139,92,246,0.3)', color: '#A78BFA', borderRadius: 3, padding: '0 2px' }}>{p}</mark>
        : p
    );
  };

  /* ── No segments fallback ── */
  if (!transcript || transcript.length === 0) {
    return (
      <Card className="glass-card">
        <CardContent sx={{ p: { xs: 3, md: 6 }, textAlign: 'center' }}>
          <Box sx={{ width: 72, height: 72, borderRadius: '50%', background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2.5 }}>
            <PersonIcon sx={{ fontSize: 36, color: '#475569' }} />
          </Box>
          <Typography variant="h6" sx={{ color: '#CBD5E1', mb: 1, fontFamily: '"Space Grotesk", sans-serif' }}>No transcript segments</Typography>
          <Typography variant="body2" sx={{ color: '#94A3B8' }}>
            {meeting?.full_text ? 'The transcript is available as plain text below.' : 'No transcript data found.'}
          </Typography>
          {meeting?.full_text && (
            <Paper elevation={0} sx={{ mt: 3, p: 3, borderRadius: '14px', background: 'rgba(139,92,246,0.05)', border: '1px solid rgba(139,92,246,0.1)', textAlign: 'left', maxHeight: 300, overflow: 'auto' }}>
              <Typography variant="body2" sx={{ lineHeight: 1.8, color: '#94A3B8' }}>{meeting.full_text}</Typography>
            </Paper>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Box className="fade-in-up">

      {/* ══ HEADER CARD ══ */}
      <Card className="glass-card" sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>

          {/* Title row */}
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} mb={2.5}>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ fontFamily: '"Space Grotesk", sans-serif', fontSize: 16 }}>
                {meeting?.title || 'Untitled Meeting'}
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="center" mt={0.6} flexWrap="wrap" useFlexGap>
                <Chip
                  label={`${transcript.length} segment${transcript.length !== 1 ? 's' : ''}`}
                  size="small"
                  sx={{ background: 'rgba(139,92,246,0.12)', color: '#A78BFA', fontWeight: 700, height: 22, fontSize: 11 }}
                />
                <Chip
                  label={`${speakerNames.length} speaker${speakerNames.length !== 1 ? 's' : ''}`}
                  size="small"
                  sx={{ background: 'rgba(6,182,212,0.1)', color: '#22D3EE', fontWeight: 700, height: 22, fontSize: 11 }}
                />
                {meeting?.created_at && (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <AccessTimeIcon sx={{ fontSize: 12, color: '#374151' }} />
                    <Typography variant="caption" sx={{ color: '#374151', fontSize: 11, fontFamily: '"JetBrains Mono", monospace' }}>
                      {new Date(meeting.created_at).toLocaleString()}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Box>

            {/* Action buttons */}
            <Stack direction="row" spacing={1} flexShrink={0}>
              <Tooltip title={copied ? 'Copied!' : 'Copy transcript'} arrow>
                <IconButton
                  onClick={handleCopy}
                  size="small"
                  sx={{
                    border: `1px solid ${copied ? 'rgba(16,185,129,0.4)' : 'rgba(139,92,246,0.2)'}`,
                    borderRadius: '10px',
                    color: copied ? '#10B981' : '#8B5CF6',
                    '&:hover': { background: 'rgba(139,92,246,0.08)' },
                  }}
                >
                  {copied ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Download .txt" arrow>
                <IconButton
                  onClick={handleDownload}
                  size="small"
                  sx={{ border: '1px solid rgba(139,92,246,0.2)', borderRadius: '10px', color: '#8B5CF6', '&:hover': { background: 'rgba(139,92,246,0.08)' } }}
                >
                  <DownloadIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {/* Search bar */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search transcript…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 17, color: '#475569' }} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')} sx={{ color: '#475569' }}>
                    <CloseIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
              sx: { borderRadius: '12px', background: 'rgba(8,8,18,0.6)', fontSize: 14 },
            }}
          />

          {/* Speaker filter chips */}
          {speakerNames.length > 1 && (
            <Stack direction="row" spacing={1} mt={2} flexWrap="wrap" useFlexGap>
              <Chip
                label="All Speakers"
                size="small"
                onClick={() => setActiveSpeaker(null)}
                sx={{
                  cursor: 'pointer',
                  background: !activeSpeaker ? 'rgba(139,92,246,0.2)' : 'transparent',
                  color: !activeSpeaker ? '#A78BFA' : '#475569',
                  border: `1px solid ${!activeSpeaker ? 'rgba(139,92,246,0.45)' : 'rgba(139,92,246,0.12)'}`,
                  fontWeight: 700, fontSize: 11, height: 26,
                  transition: 'all 0.2s',
                }}
              />
              {speakerNames.map(sp => {
                const c = colorMap[sp];
                const isActive = activeSpeaker === sp;
                return (
                  <Chip
                    key={sp}
                    label={speakerMap[sp] || sp}
                    size="small"
                    onClick={() => setActiveSpeaker(isActive ? null : sp)}
                    sx={{
                      cursor: 'pointer',
                      background: isActive ? `${c.dot}22` : 'transparent',
                      color: isActive ? c.text : '#64748B',
                      border: `1px solid ${isActive ? `${c.dot}60` : 'rgba(148,163,184,0.1)'}`,
                      fontWeight: 700, fontSize: 11, height: 26,
                      transition: 'all 0.2s',
                      '&:hover': { background: `${c.dot}14`, borderColor: c.dot, color: c.text },
                    }}
                  />
                );
              })}
            </Stack>
          )}

          {/* Result count */}
          {(search || activeSpeaker) && (
            <Typography variant="caption" sx={{ color: '#374151', mt: 1.5, display: 'block', fontFamily: '"JetBrains Mono", monospace', fontSize: 11 }}>
              {displaySegments.length} result{displaySegments.length !== 1 ? 's' : ''}
              {search ? ` for "${search}"` : ''}
              {activeSpeaker ? ` · ${speakerMap[activeSpeaker] || activeSpeaker}` : ''}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* ══ TRANSCRIPT SEGMENTS ══ */}
      <Stack spacing={1.5}>
        {displaySegments.map((seg, idx) => {
          const c = colorMap[seg.speaker] || SPEAKER_COLORS[0];
          const displayName = seg.displayName || seg.speaker;
          const initials = displayName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

          return (
            <Card
              key={idx}
              className="glass-card transcript-segment"
              sx={{
                borderLeft: `3px solid ${c.bar} !important`,
                cursor: 'default',
                '&:hover': {
                  borderLeftColor: `${c.dot} !important`,
                  boxShadow: `0 0 28px ${c.dot}18, 0 8px 28px rgba(0,0,0,0.35) !important`,
                },
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 2.5 }, '&:last-child': { pb: '18px !important' } }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">

                  {/* Speaker avatar */}
                  <Avatar
                    sx={{
                      width: 36, height: 36,
                      background: `${c.dot}1A`,
                      border: `1px solid ${c.dot}44`,
                      fontSize: 12, fontWeight: 900,
                      color: c.text, flexShrink: 0,
                      fontFamily: '"Space Grotesk", sans-serif',
                    }}
                  >
                    {initials}
                  </Avatar>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Speaker name row */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.8} flexWrap="wrap" gap={0.5}>
                      <Stack direction="row" alignItems="center" spacing={0.8}>
                        <Typography variant="caption" sx={{ color: c.text, fontWeight: 800, fontSize: 12, fontFamily: '"Space Grotesk", sans-serif' }}>
                          {displayName}
                        </Typography>
                        <Tooltip title="Rename speaker" arrow>
                          <IconButton
                            size="small"
                            onClick={() => openEditSpeaker(seg.speaker)}
                            sx={{ width: 20, height: 20, color: '#374151', '&:hover': { color: c.text, background: `${c.dot}14` } }}
                          >
                            <EditIcon sx={{ fontSize: 11 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>

                      {/* Timestamp */}
                      <Typography
                        variant="caption"
                        className="mono"
                        sx={{ color: c.text, fontSize: 11, opacity: 0.65, letterSpacing: 0.3 }}
                      >
                        {fmtTime(seg.start)}
                        {seg.end != null && seg.end !== seg.start ? ` → ${fmtTime(seg.end)}` : ''}
                      </Typography>
                    </Stack>

                    {/* Speech text */}
                    <Typography variant="body2" sx={{ color: '#CBD5E1', lineHeight: 1.78, wordBreak: 'break-word' }}>
                      {search ? highlight(seg.text, search) : seg.text}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      {/* Empty search result */}
      {displaySegments.length === 0 && (
        <Card className="glass-card" sx={{ mt: 2 }}>
          <CardContent sx={{ p: 5, textAlign: 'center' }}>
            <SearchIcon sx={{ fontSize: 42, color: '#1E293B', mb: 1.5 }} />
            <Typography variant="body1" sx={{ color: '#475569' }}>
              No results{search ? ` for "${search}"` : ''}
            </Typography>
            <Button size="small" onClick={() => { setSearch(''); setActiveSpeaker(null); }} sx={{ mt: 1.5, borderRadius: '10px', color: '#8B5CF6' }}>
              Clear filters
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ══ RENAME SPEAKER DIALOG ══ */}
      <Dialog
        open={!!editingSpeaker}
        onClose={() => setEditingSpeaker(null)}
        PaperProps={{
          sx: {
            borderRadius: '20px',
            background: 'rgba(10,10,20,0.96)',
            border: '1px solid rgba(139,92,246,0.2)',
            backdropFilter: 'blur(28px)',
            minWidth: 340,
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, pb: 0.5 }}>
          Rename Speaker
        </DialogTitle>
        <DialogContent>
          <Typography variant="caption" sx={{ color: '#475569', display: 'block', mb: 2.5 }}>
            Renaming: <strong style={{ color: '#A78BFA' }}>{editingSpeaker}</strong>
          </Typography>
          <TextField
            fullWidth
            autoFocus
            size="small"
            label="Display name"
            value={newSpeakerName}
            onChange={(e) => setNewSpeakerName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveSpeaker(); }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => setEditingSpeaker(null)} variant="outlined" sx={{ borderRadius: '10px' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveSpeaker}
            variant="contained"
            disabled={isSaving}
            sx={{ borderRadius: '10px', background: 'linear-gradient(135deg, #7C3AED, #06B6D4)', boxShadow: '0 4px 16px rgba(124,58,237,0.3)' }}
          >
            {isSaving ? 'Saving…' : 'Save Name'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
